import { createActions } from 'game/components/actionsFactory.js';

export class ActionsPlayer extends Phaser.GameObjects.Container {
  constructor({ game, scene, x, y, config }) {
    super(scene, x, y);
    this.game = game;
    scene.add.existing(this);
    this.centerX = 910;
    this.centerY = 390;
    this.characters = [];
    this.actions = [];
    this.actionsWithComplete = [];
    this.actionsNoComplete = [];
    this.sfxLooped = [];
    this.config = config;
  }

  start() {
    this.makeScene();
    const data = this.config?.actions ?? [];
    this.actions = createActions(data, this.config?.voIds ?? {}, this.config?.sfx ?? {});

    // console.log(`${this.config?.description}`, this.actions);

    this.scene.input.keyboard.on('keydown-RIGHT', this.triggerNextAction, this);
    const startActions = this.config?.start ?? [this.actions[0].id];
    for (const actionId of startActions) {
      actionId && this.runAction(actionId);
    }
  }

  makeScene() {
    let focusCharacter = false;
    let unFocusCharacter = false;
    for (const character of this.characters) {
      character.setTalking(false);
      const config = this.config.characters.find((c) => c.id === character.id);
      if (config) {
        const {
          scale,
          offset_x,
          offset_y,
          animation,
          duration = false,
          loop = true,
          ignoreIfPlaying = false,
          visible = true,
          focus,
          unFocus = false,
          unFocusMoveBelow,
          foreground,
          pause = false,
        } = config;
        character.visible = visible;
        if (!duration) {
          character.scale = scale;
          character.x = this.centerX + offset_x;
          character.y = this.centerY + offset_y;
        }
        if (animation && !pause) {
          character.play(animation, loop, ignoreIfPlaying);
        } else if (animation) {
          character.setAnimation(animation, loop, ignoreIfPlaying);
        }
        if (focus) {
          focusCharacter = {
            character,
            charConfig: { duration, scale, offset_x, offset_y },
            focusConfig: config.focus,
          };
        }
        if (unFocus) {
          unFocusCharacter = {
            character,
            charConfig: { duration, scale, offset_x, offset_y, unFocusMoveBelow },
          };
        }
        if (foreground) this.scene.children.bringToTop(character);
      } else {
        character.visible = false;
      }
    }
    this.config.id === 'emotion_notice'
      ? this.makeUnFocusCharacter(focusCharacter, unFocusCharacter)
      : this.makeFocusCharacter(focusCharacter);
  }

  makeFocusCharacter(focusCharacter) {
    // emotion_check
    // * fade in the blue background colour over everything except the 1st character
    // * zoom into the 1st character
    if (focusCharacter) {
      if (!this.focusOverlay) this.focusOverlay = this.scene.add.image(910, 390, '');
      const { character, charConfig, focusConfig } = focusCharacter;
      this.focusOverlay.setTexture(`background_${focusConfig.color}`);
      this.scene.children.bringToTop(character);
      const focusDuration = focusConfig?.duration ?? false;
      if (focusDuration) {
        this.scene.tweens.add({
          targets: this.focusOverlay,
          alpha: { from: 0, to: 1 },
          duration: focusConfig?.duration,
          onComplete: () => {
            if (charConfig.duration !== false) {
              this.tweenTarget(character, charConfig.duration, charConfig);
            }
          },
        });
      } else {
        this.focusOverlay.setAlpha(focusConfig.alpha);
      }
    } else if (this.focusOverlay) {
      this.focusOverlay.destroy();
      this.focusOverlay = null;
    }
  }

  makeUnFocusCharacter(focusCharacter, unFocusCharacter) {
    // emotion_notice
    // * zoom out from the 1st character in close up
    // * fade from blue to full background scene (to reveal the 2nd character and scene)
    // * fade in the pink background colour over everything except the 2nd character
    // * zoom into the 2nd character
    if (!this.focusOverlay) this.focusOverlay = this.scene.add.image(910, 390, '');
    this.focusOverlay.setTexture(`background_blue`);
    let { character, charConfig, focusConfig } = unFocusCharacter;
    this.scene.children.bringToTop(character);
    const unfocusCharacterOptions = this.getTweenOpts(character, charConfig.duration, charConfig);
    // const unFocusMoveBelow = charConfig?.unFocusMoveBelow ?? 0;
    // if (unFocusMoveBelow) {
    //   unfocusCharacterOptions.onComplete = () => {
    //     const moveBelow = this.getCharacter(unFocusMoveBelow);
    //     this.scene.children.moveBelow(character, moveBelow);
    //   };
    // }
    this.scene.tweens.chain({
      tweens: [
        unfocusCharacterOptions,
        {
          targets: this.focusOverlay,
          alpha: { from: 1, to: 0 },
          duration: 500,
          onComplete: () => {
            const unFocusMoveBelow = charConfig?.unFocusMoveBelow ?? 0;
            if (unFocusMoveBelow) {
              const moveBelow = this.getCharacter(unFocusMoveBelow);
              this.scene.children.moveBelow(character, moveBelow);
            } else {
              this.scene.children.moveBelow(character, this.focusOverlay);
            }
            this.scene.time.delayedCall(
              2000,
              () => {
                this.makeFocusCharacter(focusCharacter);
              },
              [],
              this
            );
          },
        },
      ],
    });
  }

  addSceneCharacters(characters) {
    this.characters = characters;
  }

  getAnimationId(characterId, animationId) {
    return this.config.animations?.[characterId]?.[animationId] ?? false;
  }

  getCharacter(name) {
    return this.characters ? this.characters.find((c) => c.id === name) : false;
  }

  getActiveCharacterPosition() {
    const character = this.getCharacter(this.config.active_character);
    if (character) {
      return { x: character.x, y: character.y };
    }
    return { x: this.centerX, y: this.centerY };
  }

  triggerNextAction() {
    this.scene.game.vo.stop();
    // for (const action of this.actionsWithComplete) {
    //   if (action.data.complete) {
    //     for (const ce of action.data.complete) {
    //       ce.id && this.runAction(ce.id);
    //     }
    //   }
    // }
  }

  playVO(action) {
    // console.log('VO', action);
    const delay = action?.delay ?? 0;
    const character = action.charId ? this.getCharacter(action.charId) : false;
    const onComplete = action.complete
      ? () => {
          character && character.setTalking(false);
          this.handleRunActionComplete(action.id);
        }
      : false;

    action.timer = this.scene.time.delayedCall(
      delay * 1000,
      () => {
        character && character?.setTalking(true);
        onComplete
          ? this.game?.vo?.play(action.voId, { onComplete, onCompleteScope: this })
          : this.game?.vo?.play(action.voId);
      },
      [],
      this
    );
  }

  playAnim(action) {
    const delay = action?.delay ?? 0;

    let onComplete = action.complete
      ? (name) => {
          if (Array.isArray(action.complete)) {
            console.warn('Please check config : ANIM type doesnt support "complete" arrays');
          } else {
            if (name === action.complete.event) {
              this.handleRunActionComplete(action.id);
            }
          }
        }
      : false;

    action.timer = this.scene.time.delayedCall(
      delay * 1000,
      () => {
        for (const { charId, animation, loop = true } of action.list) {
          const character = this.getCharacter(charId);
          if (character) {
            character.visible = true;
            onComplete
              ? character.play(animation, loop, true, onComplete, this)
              : character.play(animation, loop);
          }
          onComplete = false; // only apply to first anim in list
        }
      },
      [],
      this
    );
  }

  playEvent(action) {
    // console.log('EVENT', action);
    const delay = action?.delay ?? 0;
    action.timer = this.scene.time.delayedCall(
      delay * 1000,
      () => {
        this?.emit(action.emit);
      },
      [],
      this
    );
  }

  playMove(action) {
    // console.log('MOVE', action);
    const delay = action?.delay ?? 0;
    action.timer = this.scene.time.delayedCall(
      delay * 1000,
      () => {
        for (const { duration, charId, scale, offset_x, offset_y, complete } of action.list) {
          const character = this.getCharacter(charId);
          if (character) {
            if (duration) {
              this.tweenTarget(character, duration, { scale, offset_x, offset_y }, complete);
            } else {
              scale && (character.scale = scale);
              offset_x && (character.x = this.centerX + offset_x);
              offset_y && (character.y = this.centerY + offset_y);
            }
          }
        }
      },
      [],
      this
    );
  }

  playFocus(action) {
    // console.log('FOCUS', action);
    if (this.focusOverlay) {
      this.focusOverlay.setTexture(`background_${action.focus.color}`);
      this.focusOverlay.setAlpha(action.focus.alpha);
    }
  }

  playBringToTop(action) {
    const character = action.charId ? this.getCharacter(action.charId) : false;
    character && this.scene.children.bringToTop(character);
  }

  playSFX(action) {
    const sfx = action?.sfx ?? {};
    const { id = false, volume = 0.5, loop = false } = sfx;
    const delay = action?.delay ?? 0;

    const onComplete = action.complete
      ? () => {
          this.handleRunActionComplete(action.id);
        }
      : false;

    action.timer = this.scene.time.delayedCall(
      delay * 1000,
      () => {
        if (id) {
          const sfx = this.game?.soundController?.playSFX('sfx', id, { volume, loop });
          if (onComplete && sfx) {
            sfx.once('complete', onComplete, this);
          }
          loop && this.sfxLooped.push(sfx);
        }
      },
      [],
      this
    );
  }

  stopSFX(action) {
    this.game?.soundController.stopSFX(action.sfx.id);
  }

  runAction(actionId) {
    if (this.actions) {
      const index = this.actions.findIndex((c) => actionId === c.id);
      const action = this.actions.splice(index, 1)[0];
      action && action?.run(this.game, this);
      if (action.data.complete) {
        this.actionsWithComplete.push(action);
      } else {
        this.actionsNoComplete.push(action);
      }
      if (action.data.start) {
        for (const ce of action.data.start) {
          ce.id && this.runAction(ce.id);
        }
      }
    }
  }

  handleRunActionComplete(actionId) {
    if (this.actionsWithComplete) {
      const index = this.actionsWithComplete.findIndex((c) => actionId === c.id);
      const action = this.actionsWithComplete.splice(index, 1)[0];
      if (action.data.complete) {
        if (Array.isArray(action.data.complete)) {
          for (const act of action.data.complete) {
            act.id && this.runAction(act.id);
          }
        } else {
          action.data.complete.id && this.runAction(action.data.complete.id);
        }
      }
      this.cleanAction(action);
    }
  }

  tweenTarget(target, duration, { scale, offset_x, offset_y }, completeId = null) {
    // const opts = {
    //   targets: target,
    //   x: typeof offset_x === 'number' ? this.centerX + offset_x : target.x,
    //   y: typeof offset_y === 'number' ? this.centerY + offset_y : target.y,
    //   scaleX: typeof scale === 'number' ? scale : target.scaleX,
    //   scaleY: typeof scale === 'number' ? scale : target.scaleY,
    //   duration,
    // };
    const opts = this.getTweenOpts(target, duration, { scale, offset_x, offset_y });
    if (completeId) {
      opts.onComplete = () => {
        // console.log(`tweenTarget complete ${completeId}`);
        this.runAction(completeId);
      };
    }
    this.scene.tweens.add(opts);
  }

  cleanAction(action) {
    if (action) {
      // console.log('cleanAction', action);
      action.timer && action.timer.remove();
      action.data = null;
      action.timer = null;
      action.run = null;
    }
    action = null;
  }

  cleanActions(ary) {
    if (!ary) return;
    for (const action of ary) {
      this.cleanAction(action);
    }
  }

  destroy(fromScene) {
    this.cleanActions(this.actions);
    this.actions = null;
    this.cleanActions(this.actionsWithComplete);
    this.actionsWithComplete = null;
    this.cleanActions(this.actionsNoComplete);
    this.actionsNoComplete = null;

    if (this.sfxLooped) {
      for (let sfx of this.sfxLooped) {
        sfx.stop();
        sfx = null;
      }
    }
    this.sfxLooped = null;

    if (this.scene) {
      this.scene.game.vo.stop();
      this.scene?.input?.keyboard?.off('keydown-RIGHT', this.triggerNextAction, this);
    }
    if (this.focusOverlay) {
      this.focusOverlay.destroy();
      this.focusOverlay = null;
    }
    if (this.characters) {
      this.characters.forEach((charId) => {
        const character = this.getCharacter(charId);
        character && character.setTalking(false);
      });
    }
    this.removeAllListeners();
    this.characters = null;
    this.game = null;
    this.sfxLooped = null;
    this.scene?.time?.removeAllEvents();
    super.destroy(fromScene);
  }

  getTweenOpts(target, duration, { scale, offset_x, offset_y }) {
    return {
      targets: target,
      x: typeof offset_x === 'number' ? this.centerX + offset_x : target.x,
      y: typeof offset_y === 'number' ? this.centerY + offset_y : target.y,
      scaleX: typeof scale === 'number' ? scale : target.scaleX,
      scaleY: typeof scale === 'number' ? scale : target.scaleY,
      duration,
    };
  }
}
